import React, { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { CheckCircle2, XCircle, AlertCircle, ExternalLink, Plus } from "lucide-react"
import { Mailbox } from '@/hooks/useMailboxes'
import { useMailboxProviders } from '@/hooks/useMailboxProviders'
import { AddProviderDialog } from './AddProviderDialog'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { formatSignatureForDisplay, formatSignatureForSync } from '@/lib/signature-formatter'

interface MailboxEditDialogProps {
  mailbox: Mailbox | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpdate: () => void
}

export function MailboxEditDialog({ mailbox, open, onOpenChange, onUpdate }: MailboxEditDialogProps) {
  const [messagePerDay, setMessagePerDay] = useState('')
  const [minimumTimeGap, setMinimumTimeGap] = useState('')
  const [fromName, setFromName] = useState('')
  const [signature, setSignature] = useState('')
  const [customTrackingDomain, setCustomTrackingDomain] = useState('')
  const [username, setUsername] = useState('')
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState('')
  const [imapHost, setImapHost] = useState('')
  const [imapPort, setImapPort] = useState('')
  const [differentReplyToAddress, setDifferentReplyToAddress] = useState('')
  const [isDifferentImapAccount, setIsDifferentImapAccount] = useState(false)
  
  // New fields
  const [appPassword, setAppPassword] = useState('')
  const [emailPassword, setEmailPassword] = useState('')
  const [mailboxProvider, setMailboxProvider] = useState('')
  const [mailboxCost, setMailboxCost] = useState('')
  const [mailboxRegistrationDate, setMailboxRegistrationDate] = useState('')
  const [warmupEnabledDate, setWarmupEnabledDate] = useState('')
  const [purchaseDate, setPurchaseDate] = useState('')
  const [mailboxStatus, setMailboxStatus] = useState('')
  const [subStatus, setSubStatus] = useState('')
  const [batchNumber, setBatchNumber] = useState('')
  const [notes, setNotes] = useState('')
  const [showAddProvider, setShowAddProvider] = useState(false)
  const [warmupStatus, setWarmupStatus] = useState('')
  const [profilePictureUrl, setProfilePictureUrl] = useState('')
  const [isWorkspaceAdmin, setIsWorkspaceAdmin] = useState(false)
  
  const [isUpdating, setIsUpdating] = useState(false)
  const { toast } = useToast()
  const { providers, refetch: refetchProviders } = useMailboxProviders()

  // Helper function to map UI status values to database values
  const mapStatusToDatabase = (uiStatus: string): string => {
    switch (uiStatus) {
      case 'Active':
        return 'ACTIVE'
      case 'Inactive':
        return 'INACTIVE'
      case 'Deactivated':
        return 'DEACTIVATED'
      case 'ORDERED/PURCHASED':
        return 'ORDERED/PURCHASED'
      case 'WAITING FOR UPLOAD':
        return 'WAITING FOR UPLOAD'
      default:
        return 'ORDERED/PURCHASED' // Default fallback
    }
  }

  // Helper function to map database status values to UI values
  const mapStatusFromDatabase = (dbStatus: string): string => {
    switch (dbStatus) {
      case 'ACTIVE':
        return 'Active'
      case 'INACTIVE':
        return 'Inactive'
      case 'DEACTIVATED':
        return 'Deactivated'
      case 'ORDERED/PURCHASED':
        return 'ORDERED/PURCHASED'
      case 'WAITING FOR UPLOAD':
        return 'WAITING FOR UPLOAD'
      default:
        return 'ORDERED/PURCHASED' // Default fallback
    }
  }

  React.useEffect(() => {
    if (mailbox) {
      setMessagePerDay(mailbox.message_per_day?.toString() || '0')
      setMinimumTimeGap('50') // Default time gap in minutes
      setFromName(mailbox.from_name || '')
      // Format signature for display (preserve line breaks but remove HTML)
      const displaySignature = formatSignatureForDisplay(mailbox.signature)
      setSignature(displaySignature)
      setCustomTrackingDomain(mailbox.custom_tracking_domain || '')
      setUsername(mailbox.username || '')
      setSmtpHost(mailbox.smtp_host || '')
      setSmtpPort(mailbox.smtp_port?.toString() || '')
      setImapHost(mailbox.imap_host || '')
      setImapPort(mailbox.imap_port?.toString() || '')
      setDifferentReplyToAddress(mailbox.different_reply_to_address || '')
      setIsDifferentImapAccount(mailbox.is_different_imap_account || false)
      
      // New fields - populate from mailbox data
      setAppPassword('') // For security, don't pre-fill passwords
      setEmailPassword('') // For security, don't pre-fill passwords
      setMailboxProvider(mailbox.provider || '')
      setMailboxCost(mailbox.mailbox_cost || '')
      setMailboxRegistrationDate(mailbox.registration_date || (mailbox.created_at ? new Date(mailbox.created_at).toISOString().split('T')[0] : ''))
      setWarmupEnabledDate(mailbox.warmup_enabled_date || '')
      setPurchaseDate(mailbox.purchase_date || '')
      // Fix status initialization - properly map database status to UI status
      const dbStatus = mailbox.status || 'ACTIVE'
      const initialStatus = mapStatusFromDatabase(dbStatus)
      console.log('Mailbox debug:', {
        id: mailbox.id,
        email: mailbox.email,
        status: mailbox.status,
        api_sync_status: mailbox.api_sync_status,
        warmup_status: mailbox.warmup_status,
        warmup_reputation: mailbox.warmup_reputation,
        deactivated_at: mailbox.deactivated_at,
        dbStatus,
        initialStatus
      })
      setMailboxStatus(initialStatus)
      // Set sub-status based on database value or get default for status
      let defaultSubStatus = 'Ready to use'
      if (initialStatus === 'Deactivated') {
        defaultSubStatus = 'Deactivated'
      } else if (initialStatus === 'ORDERED/PURCHASED') {
        defaultSubStatus = 'Pending delivery'
      } else if (initialStatus === 'WAITING FOR UPLOAD') {
        defaultSubStatus = 'Awaiting credentials'
      }
      const dbSubStatus = mailbox.sub_status || defaultSubStatus
      setSubStatus(dbSubStatus)
      // Set batch number from database or default
      setBatchNumber(mailbox.batch_number?.toString() || '1')
      setMinimumTimeGap(mailbox.minimum_time_gap?.toString() || '50')
      setNotes(mailbox.notes || '')
      setWarmupStatus(mailbox.warmup_status || 'inactive')
      setProfilePictureUrl(mailbox.profile_picture_url || '')
      setIsWorkspaceAdmin(mailbox.is_workspace_admin || false)
    }
  }, [mailbox])

  // Get appropriate sub-status options based on main status
  const getSubStatusOptions = () => {
    switch (mailboxStatus) {
      case 'Active':
      case 'Inactive':
        return [
          { value: 'Warming up', label: 'Warming up' },
          { value: 'Ready to use', label: 'Ready to use' },
          { value: 'Recovering', label: 'Recovering' },
          { value: 'Burned', label: 'Burned' }
        ]
      case 'Deactivated':
        return [
          { value: 'Deactivated', label: 'Deactivated' }
        ]
      case 'ORDERED/PURCHASED':
        return [
          { value: 'Pending delivery', label: 'Pending delivery' },
          { value: 'Awaiting setup', label: 'Awaiting setup' },
          { value: 'Order confirmed', label: 'Order confirmed' }
        ]
      case 'WAITING FOR UPLOAD':
        return [
          { value: 'Awaiting credentials', label: 'Awaiting credentials' },
          { value: 'Ready to upload', label: 'Ready to upload' }
        ]
      default:
        return [
          { value: 'Warming up', label: 'Warming up' },
          { value: 'Ready to use', label: 'Ready to use' }
        ]
    }
  }

  // Effect to sync sub-status when main status changes
  React.useEffect(() => {
    const options = getSubStatusOptions()
    const validSubStatus = options.find(opt => opt.value === subStatus)
    
    // If current sub-status is not valid for the new status, set to first option
    if (!validSubStatus && options.length > 0) {
      setSubStatus(options[0].value)
    }
  }, [mailboxStatus])

  const handleUpdate = async () => {
    if (!mailbox) return

    setIsUpdating(true)
    
    // Import logBulkOperation
    const { logBulkOperation, updateBulkOperation } = await import('@/hooks/useBulkOperations')
    
    let operation: any = null
    
    try {
      // Log the individual update operation with previous vs new values
      operation = await logBulkOperation(
        'individual_update',
        1,
        { 
          message_per_day: `${mailbox.message_per_day || 0} → ${parseInt(messagePerDay) || 0}`,
          min_time_gap: `50 → ${parseInt(minimumTimeGap) || 50}`,
          mailbox_email: mailbox.email 
        },
        'User'
      )
      // First check if mailbox is being deactivated
      const isBeingDeactivated = mailboxStatus === 'Deactivated'
      const smartleadApiKey = '19b11d82-9894-41db-8ca6-61988c208a29_80kb8ml'
      
      // Only update Smartlead if mailbox is not being deactivated and has a smartlead_id
      if (!isBeingDeactivated && mailbox.smartlead_id) {
        console.log('Updating mailbox with smartlead_id:', mailbox.smartlead_id)
        console.log('Changes detected:', {
          warmupStatus: warmupStatus !== mailbox.warmup_status ? `${mailbox.warmup_status} → ${warmupStatus}` : 'no change',
          signature: signature !== mailbox.signature ? 'changed' : 'no change',
          customTrackingDomain: customTrackingDomain !== mailbox.custom_tracking_domain ? 'changed' : 'no change',
          fromName: fromName !== mailbox.from_name ? 'changed' : 'no change'
        })

        // Prepare Smartlead update payloads - split into warmup and general updates
        const warmupPayload: any = {}
        const generalPayload: any = {
          max_email_per_day: parseInt(messagePerDay) || 0,
          client_id: mailbox.client_id,
          time_to_wait_in_mins: parseInt(minimumTimeGap) || 50
        }

        // Add warmup status if it's being changed
        if (warmupStatus && warmupStatus !== mailbox.warmup_status) {
          const warmupEnabled = warmupStatus === 'active'
          warmupPayload.warmup_enabled = warmupEnabled
          
          // Only add required fields when enabling warmup
          if (warmupEnabled) {
            warmupPayload.daily_warmup_limit = 50 // Default warmup limit
            warmupPayload.ramp_up = true // Enable ramp up
            warmupPayload.ramp_up_value = 5 // Ramp up by 5 emails per day
            warmupPayload.reply_rate = 30 // 30% reply rate
            warmupPayload.warmup_tag = 'OutboundBuddy' // Tag for warmup emails
          }
          
          console.log('Warmup status change detected:', {
            oldValue: mailbox.warmup_status,
            newValue: warmupStatus,
            smartleadValue: warmupEnabled,
            payload: warmupPayload
          })
        }

        // Add signature if it's being changed (format for sync)
        if (signature && signature !== formatSignatureForDisplay(mailbox.signature)) {
          generalPayload.signature = formatSignatureForSync(signature)
        }

        // Add custom tracking domain if it's being changed
        if (customTrackingDomain && customTrackingDomain !== mailbox.custom_tracking_domain) {
          generalPayload.custom_tracking_url = customTrackingDomain
        }

        // Add from name if it's being changed
        if (fromName && fromName !== mailbox.from_name) {
          generalPayload.from_name = fromName
        }

        // Combine all updates into a single payload for complete sync
        // Put generalPayload LAST so it doesn't get overwritten by warmupPayload
        const allUpdates = {
          ...warmupPayload,
          ...generalPayload
        }

        // Debug: Log what's being sent
        console.log('🔍 Debug - Update Payloads:')
        console.log('  Warmup Payload:', warmupPayload)
        console.log('  General Payload:', generalPayload)
        console.log('  All Updates:', allUpdates)
        console.log('  Message Per Day from UI:', messagePerDay)
        console.log('  Message Per Day from DB:', mailbox.message_per_day)
        console.log('  All Updates Keys:', Object.keys(allUpdates))
        console.log('  Has max_email_per_day?', 'max_email_per_day' in allUpdates)
        console.log('  max_email_per_day value:', allUpdates.max_email_per_day)

        // Always sync ALL fields to Smartlead to ensure consistency
        if (Object.keys(allUpdates).length > 0) {
          console.log('Syncing ALL fields to Smartlead for consistency:', allUpdates)
          
          const smartleadResponse = await supabase.functions.invoke('smartlead-mailboxes', {
            body: {
              action: 'update',
              email_account_id: mailbox.smartlead_id,
              updates: allUpdates
            }
          })

          console.log('Smartlead full sync response:', smartleadResponse)
          
          if (smartleadResponse.error) {
            console.error('Smartlead sync error:', smartleadResponse.error)
            
            if (smartleadResponse.error.message?.includes('404') || smartleadResponse.error.message?.includes('not found')) {
              console.warn(`Mailbox ${mailbox.smartlead_id} not found in Smartlead`)
              toast({
                title: "Warning",
                description: `Mailbox ID ${mailbox.smartlead_id} not found in Smartlead. Database updated, but Smartlead sync failed.`,
                variant: "destructive"
              })
            } else {
              throw new Error(`Smartlead sync error: ${smartleadResponse.error.message}`)
            }
          } else {
            console.log('Smartlead full sync success:', smartleadResponse.data)
          }
        } else {
          console.log('No changes detected for Smartlead sync, skipping API calls')
        }
      } else if (isBeingDeactivated) {
        console.log('Mailbox being deactivated - skipping Smartlead update')
      }

      // Then update local database
      const dbStatus = mapStatusToDatabase(mailboxStatus)
      const updateData: any = {
        message_per_day: parseInt(messagePerDay) || 0,
        minimum_time_gap: parseInt(minimumTimeGap) || null,
        from_name: fromName,
        signature: signature,
        custom_tracking_domain: customTrackingDomain,
        username: username,
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort) || null,
        imap_host: imapHost,
        imap_port: parseInt(imapPort) || null,
        different_reply_to_address: differentReplyToAddress,
        is_different_imap_account: isDifferentImapAccount,
        provider: mailboxProvider,
        status: dbStatus, // Use mapped database status
        // New fields
        app_password: appPassword || null,
        email_password: emailPassword || null,
        mailbox_cost: mailboxCost || null,
        purchase_date: purchaseDate || null,
        warmup_enabled_date: warmupEnabledDate || null,
        registration_date: mailboxRegistrationDate || null,
        // Sub-status and batch number
        sub_status: subStatus,
        batch_number: parseInt(batchNumber) || 1,
        notes: notes,
        warmup_status: warmupStatus,
        // Profile and admin fields
        profile_picture_url: profilePictureUrl || null,
        is_workspace_admin: isWorkspaceAdmin,
        updated_at: new Date().toISOString()
      }

      console.log('Database update payload:', {
        mailboxId: mailbox.id,
        email: mailbox.email,
        uiStatus: mailboxStatus,
        dbStatus: dbStatus,
        isBeingDeactivated: isBeingDeactivated,
        updateData
      })

      // If mailbox is being deactivated, update api_sync_status and set deactivated_at
      if (isBeingDeactivated) {
        updateData.api_sync_status = 'DEACTIVATED'
        updateData.deactivated_at = new Date().toISOString()
        updateData.warmup_status = 'INACTIVE'
      }

      const { error } = await supabase
        .from('mailboxes')
        .update(updateData)
        .eq('id', mailbox.id)

      if (error) {
        console.error('Database update error:', error)
        throw new Error(`Database update failed: ${error.message}`)
      }

      // Update operation with success
      if (operation) {
        await updateBulkOperation(operation.id, {
          successful_count: 1,
          failed_count: 0,
        })
      }

      toast({
        title: "Mailbox Updated",
        description: "Mailbox settings have been successfully updated and synced to Smartlead immediately.",
      })

      onUpdate()
      onOpenChange(false)
    } catch (error) {
      console.error('Error updating mailbox:', error)
      
      // Update operation with failure
      if (operation) {
        await updateBulkOperation(operation.id, {
          successful_count: 0,
          failed_count: 1,
          errors: [`${mailbox.email}: ${error instanceof Error ? error.message : 'Unknown error'}`],
        })
      }
      
      toast({
        title: "Update Failed",
        description: "Failed to update mailbox settings. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsUpdating(false)
    }
  }

  const getWarmupStatusBadge = (status: string) => {
    // If mailbox is shut down, warmup cannot be active
    if (mailboxStatus === 'Deactivated' || mailbox.api_sync_status === 'DEACTIVATED') {
      return <Badge variant="destructive">INACTIVE</Badge>
    }
    
    const normalizedStatus = status?.toLowerCase()
    if (normalizedStatus === 'active' || normalizedStatus === 'enabled') {
      return <Badge className="bg-green-500 text-white">ACTIVE</Badge>
    }
    if (normalizedStatus === 'inactive') {
      return <Badge variant="destructive">INACTIVE</Badge>
    }
    return <Badge variant="secondary">UNKNOWN</Badge>
  }

  const getReputationColor = (reputation: number) => {
    if (reputation >= 80) return 'text-green-600'
    if (reputation >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getSmartleadStatus = () => {
    if (!mailbox.smartlead_id) {
      return {
        icon: <XCircle className="h-4 w-4 text-red-500" />,
        label: "Not Connected",
        variant: "destructive" as const
      }
    }
    
    if (mailbox.api_sync_status === 'DEACTIVATED') {
      return {
        icon: <XCircle className="h-4 w-4 text-red-500" />,
        label: "Deactivated",
        variant: "destructive" as const
      }
    }
    
    if (mailbox.api_sync_status === 'SUCCESS') {
      return {
        icon: <CheckCircle2 className="h-4 w-4 text-green-500" />,
        label: "Active",
        variant: "default" as const
      }
    }
    
    return {
      icon: <AlertCircle className="h-4 w-4 text-yellow-500" />,
      label: "Sync Issues",
      variant: "secondary" as const
    }
  }

  const getDaysWarmingUp = () => {
    if (!mailbox.created_at) return 0
    const startDate = new Date(mailbox.created_at)
    const currentDate = new Date()
    const diffTime = Math.abs(currentDate.getTime() - startDate.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  // Mailbox status options that can be properly mapped to database constraints
  const statusOptions = ['Active', 'Inactive', 'Deactivated', 'ORDERED/PURCHASED', 'WAITING FOR UPLOAD']

  if (!mailbox) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] max-w-4xl max-h-[85vh] overflow-y-auto flex flex-col">
        <DialogHeader className="pb-4 border-b">
          <DialogTitle className="text-xl font-semibold">Edit Mailbox Settings</DialogTitle>
          <div className="space-y-2 pt-2">
            <p className="text-sm font-medium text-foreground">
              {mailbox.email}
            </p>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="bg-muted/50 px-2 py-1 rounded">Client ID: {mailbox.client_id || 'N/A'}</span>
              <span className="bg-muted/50 px-2 py-1 rounded">Smartlead ID: {mailbox.smartlead_id || 'N/A'}</span>
            </div>
          </div>
        </DialogHeader>
        
        <div className="overflow-y-auto pr-2 space-y-8">
          {/* Connected Services */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              Connected Services
            </h3>
            <div className="flex gap-3">
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg border bg-card">
                {getSmartleadStatus().icon}
                <span className="text-sm font-medium">Smartlead</span>
                <Badge variant={getSmartleadStatus().variant} className="text-xs">
                  {getSmartleadStatus().label}
                </Badge>
                {mailbox.smartlead_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 ml-auto"
                    onClick={() => window.open(`https://app.smartlead.ai/app/email-account/${mailbox.smartlead_id}/general`, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Current Status */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              Current Status
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                <Select value={mailboxStatus} onValueChange={setMailboxStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Sub-Status</Label>
                <Select value={subStatus} onValueChange={setSubStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getSubStatusOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Batch Number</Label>
                <Select value={batchNumber} onValueChange={setBatchNumber}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Second row for additional status fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Warmup Status</Label>
                <div className="pt-1">
                  {getWarmupStatusBadge(mailbox.warmup_status || 'inactive')}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  {mailbox.api_sync_status === 'DEACTIVATED' ? 'Deactivation Date' : 'Purchase Date'}
                </Label>
                <div className="text-sm text-foreground pt-1">
                  {mailbox.api_sync_status === 'DEACTIVATED' && mailbox.deactivated_at
                    ? new Date(mailbox.deactivated_at).toLocaleDateString()
                    : mailbox.purchase_date 
                    ? new Date(mailbox.purchase_date).toLocaleDateString() 
                    : 'N/A'}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Warmup Reputation</Label>
                <div className={`text-2xl font-bold ${
                  mailboxStatus === 'Deactivated' || mailbox.api_sync_status === 'DEACTIVATED' 
                    ? 'text-muted-foreground' 
                    : getReputationColor(parseInt(mailbox.warmup_reputation || '0'))
                }`}>
                  {mailboxStatus === 'Deactivated' || mailbox.api_sync_status === 'DEACTIVATED' 
                    ? 'N/A' 
                    : `${mailbox.warmup_reputation || 0}%`}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Performance & Additional Metrics */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              Performance & Additional Metrics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Days Warming Up</Label>
                <div className="text-2xl font-bold text-foreground">
                  {getDaysWarmingUp()}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Daily Sent Count</Label>
                <div className="text-2xl font-bold text-foreground">
                  {mailbox.daily_sent_count || 0}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Emails Sent (Total)</Label>
                <div className="text-2xl font-bold text-foreground">
                  {mailbox.warmup_sent_total || 0}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Registration Date</Label>
                <div className="text-sm text-foreground pt-1">
                  {mailbox.created_at 
                    ? new Date(mailbox.created_at).toLocaleDateString() 
                    : 'N/A'}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Email Replies (Total)</Label>
                <div className="text-xl font-semibold text-foreground">
                  0 {/* This would need to be added to database */}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">Emails Bounced (Total)</Label>
                <div className="text-xl font-semibold text-foreground">
                  0 {/* This would need to be added to database */}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Email Sending Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Email Sending Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="messagePerDay" className="text-sm font-medium">
                  Message Per Day
                </Label>
                <Input
                  id="messagePerDay"
                  type="number"
                  value={messagePerDay}
                  onChange={(e) => setMessagePerDay(e.target.value)}
                  className="mt-1"
                  min="0"
                  max="1000"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum number of emails to send per day
                </p>
              </div>

              <div>
                <Label htmlFor="minimumTimeGap" className="text-sm font-medium">
                  Minimum Time Gap (minutes)
                </Label>
                <Input
                  id="minimumTimeGap"
                  type="number"
                  value={minimumTimeGap}
                  onChange={(e) => setMinimumTimeGap(e.target.value)}
                  className="mt-1"
                  min="1"
                  max="1440"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum time gap between emails in minutes
                </p>
              </div>

              <div>
                <Label htmlFor="warmupStatus" className="text-sm font-medium">
                  Warmup Status
                </Label>
                <Select value={warmupStatus} onValueChange={setWarmupStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Enable</SelectItem>
                    <SelectItem value="inactive">Disable</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Enable or disable email warmup for this mailbox
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="fromName" className="text-sm font-medium">
                From Name
              </Label>
              <Input
                id="fromName"
                type="text"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                className="mt-1"
                placeholder="Display name for emails"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Name displayed in sent emails
              </p>
            </div>
          </div>

          <Separator />

          {/* Email Signature */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Email Signature</h3>
            <div>
              <Label htmlFor="signature" className="text-sm font-medium">
                Email Signature
              </Label>
              <Textarea
                id="signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                className="mt-1"
                placeholder="Plain text signature"
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Plain text signature for outgoing emails
              </p>
            </div>
          </div>

          <Separator />

          {/* SMTP Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">SMTP Settings (Sending Emails)</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="username" className="text-sm font-medium">
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-1"
                  placeholder="Email username"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  SMTP/IMAP username (usually same as email)
                </p>
              </div>
              <div>
                <Label htmlFor="emailPassword" className="text-sm font-medium">
                  Email Password
                </Label>
                <Input
                  id="emailPassword"
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  className="mt-1"
                  placeholder="Email password"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Email account password
                </p>
              </div>
              <div>
                <Label htmlFor="appPassword" className="text-sm font-medium">
                  App Password
                </Label>
                <Input
                  id="appPassword"
                  type="password"
                  value={appPassword}
                  onChange={(e) => setAppPassword(e.target.value)}
                  className="mt-1"
                  placeholder="App-specific password"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  App-specific password for 2FA accounts
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="mailboxProvider" className="text-sm font-medium">
                  Mailbox Provider
                </Label>
                <div className="flex gap-2 mt-1">
                  <Select value={mailboxProvider} onValueChange={setMailboxProvider}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover">
                      {providers.map((provider) => (
                        <SelectItem key={provider.id} value={provider.name}>
                          {provider.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddProvider(true)}
                    className="px-3"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Email service provider
                </p>
              </div>
              <div>
                <Label htmlFor="mailboxCost" className="text-sm font-medium">
                  Mailbox Cost
                </Label>
                <Input
                  id="mailboxCost"
                  type="text"
                  value={mailboxCost}
                  onChange={(e) => setMailboxCost(e.target.value)}
                  className="mt-1"
                  placeholder="$0.00/month"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Monthly cost of this mailbox
                </p>
              </div>
              <div>
                <Label htmlFor="purchaseDate" className="text-sm font-medium">
                  Purchase Date
                </Label>
                <Input
                  id="purchaseDate"
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  When this mailbox was purchased
                </p>
              </div>
              <div>
                <Label htmlFor="warmupEnabledDate" className="text-sm font-medium">
                  Warmup Enabled Date
                </Label>
                <Input
                  id="warmupEnabledDate"
                  type="date"
                  value={warmupEnabledDate}
                  onChange={(e) => setWarmupEnabledDate(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  When warmup was started
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smtpHost" className="text-sm font-medium">
                  SMTP Host
                </Label>
                <Input
                  id="smtpHost"
                  type="text"
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  className="mt-1"
                  placeholder="smtp.gmail.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  SMTP server hostname
                </p>
              </div>
              <div>
                <Label htmlFor="smtpPort" className="text-sm font-medium">
                  SMTP Port
                </Label>
                <Input
                  id="smtpPort"
                  type="number"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                  className="mt-1"
                  placeholder="465"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  SMTP port number
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* IMAP Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">IMAP Settings (Receiving Emails)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="imapHost" className="text-sm font-medium">
                  IMAP Host
                </Label>
                <Input
                  id="imapHost"
                  type="text"
                  value={imapHost}
                  onChange={(e) => setImapHost(e.target.value)}
                  className="mt-1"
                  placeholder="imap.gmail.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  IMAP server hostname
                </p>
              </div>
              <div>
                <Label htmlFor="imapPort" className="text-sm font-medium">
                  IMAP Port
                </Label>
                <Input
                  id="imapPort"
                  type="number"
                  value={imapPort}
                  onChange={(e) => setImapPort(e.target.value)}
                  className="mt-1"
                  placeholder="993"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  IMAP port number
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="isDifferentImapAccount"
                checked={isDifferentImapAccount}
                onCheckedChange={(checked) => setIsDifferentImapAccount(checked as boolean)}
              />
              <Label htmlFor="isDifferentImapAccount" className="text-sm font-medium">
                Use different email accounts for receiving emails
              </Label>
            </div>
          </div>

          <Separator />

          {/* Advanced Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Advanced Settings</h3>
            <div>
              <Label htmlFor="customTrackingDomain" className="text-sm font-medium">
                Custom Tracking Domain
              </Label>
              <Input
                id="customTrackingDomain"
                type="text"
                value={customTrackingDomain}
                onChange={(e) => setCustomTrackingDomain(e.target.value)}
                className="mt-1"
                placeholder="tracking.yourdomain.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Custom domain for email tracking
              </p>
            </div>

            <div>
              <Label htmlFor="differentReplyToAddress" className="text-sm font-medium">
                Different Reply-To Address
              </Label>
              <Input
                id="differentReplyToAddress"
                type="email"
                value={differentReplyToAddress}
                onChange={(e) => setDifferentReplyToAddress(e.target.value)}
                className="mt-1"
                placeholder="replies@yourdomain.com"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Set a different email address for replies
              </p>
            </div>
          </div>

          <Separator />

          {/* Notes Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Notes</h3>
            <div>
              <Label htmlFor="notes" className="text-sm font-medium">
                Notes
              </Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1"
                placeholder="Add notes about this mailbox..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Add any notes or comments about this mailbox
              </p>
            </div>
          </div>

          <Separator />

          {/* Profile & Admin Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Profile & Workspace Settings</h3>
            
            {/* Profile Picture URL */}
            <div>
              <Label htmlFor="profilePicture" className="text-sm font-medium">
                Profile Picture URL
              </Label>
              <Input
                id="profilePicture"
                value={profilePictureUrl}
                onChange={(e) => setProfilePictureUrl(e.target.value)}
                className="mt-1"
                placeholder="https://example.com/photo.jpg"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL to the user's profile picture for this mailbox
              </p>
            </div>

            {/* Workspace Admin */}
            <div className="flex items-center justify-between space-x-2 py-2 rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label htmlFor="workspaceAdmin" className="text-sm font-medium cursor-pointer">
                  Google Workspace Admin
                </Label>
                <p className="text-xs text-muted-foreground">
                  Mark this mailbox as the workspace administrator for its domain
                </p>
              </div>
              <Checkbox
                id="workspaceAdmin"
                checked={isWorkspaceAdmin}
                onCheckedChange={(checked) => setIsWorkspaceAdmin(checked as boolean)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </div>
      </DialogContent>
      
      <AddProviderDialog 
        open={showAddProvider}
        onOpenChange={setShowAddProvider}
        onProviderAdded={(provider) => {
          setMailboxProvider(provider.name)
          refetchProviders()
        }}
      />
    </Dialog>
  )
}